import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import {
  TravelDocumentSalesLiteModel,
  useGetTravelDocumentsSales,
} from 'api-hooks/sale/travel-document';
import { CheckCircleFillSVG } from 'common/assets';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import stylesColors from 'common/styles/styles.colors';
import { capitalize, string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import {
  commonColumnTable,
  commonSaleColumnTable,
} from 'legacy-modules/common/column';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import SaleListTabs from './components/sale-list-tabs';
import DocumentPillStatus from './components/status';
import TravelDocumentSalesTable from './components/table';
import {
  BillingAddressTagname,
  CustomerListTagname,
} from '../component/list-tagname';
import SalePillStatus from '../component/status';

export default function TravelDocumentSalesList() {
  const { t } = useTranslation();
  const formatDate = useDate();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const _columns = React.useMemo<IColumn<TravelDocumentSalesLiteModel>[]>(
    () => [
      {
        header: t('document:document_status'),
        accessorKey: 'documentSaleStatus',
        minSize: 280,
        cell: ({ getValue }) => (
          <DocumentPillStatus value={getValue() as string} />
        ),
      },
      {
        header: t('common:payment_status'),
        accessorKey: 'paymentStatus',
        minSize: 150,
        sortName: 'payment_status',
        cell: ({ getValue }) => <SalePillStatus value={getValue() as string} />,
      },
      {
        header: t('common:sale_status'),
        accessorKey: 'status',
        minSize: 150,
        sortName: 'status',
        cell: ({ getValue }) => t(`status:${getValue() as string}`),
      },
      {
        header: t('common:complete'),
        accessorKey: 'isCompleted',
        minSize: 150,
        sortName: 'is_completed',
        cell: ({ row }) => {
          const {
            original: { isCompleted },
          } = row;
          if (!isCompleted) {
            return null;
          }
          return <CheckCircleFillSVG color={stylesColors.primaryDefault} />;
        },
      },
      {
        header: t('document:transaction_at'),
        accessorKey: 'transactionAt',
        minSize: 150,
        sortName: 'transaction_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:invoice_number'),
        accessorKey: 'invoiceNumber',
        minSize: 200,
        sortName: 'invoice_number',
      },
      {
        header: t('common:customer'),
        accessorKey: 'customer',
        minSize: 250,
        cell: ({ row }) => {
          const customer = row.original.customer;
          return <CustomerListTagname customer={customer} />;
        },
      },
      {
        header: t('common:group_code'),
        accessorKey: 'code',
        minSize: 250,
        sortName: 'code',
      },
      {
        header: t('common:project'),
        accessorKey: 'project',
        minSize: 150,
        cell: ({ row }) => row.original.project?.code || '-',
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 250,
        sortName: 'description',
      },
      {
        header: t('document:document_type'),
        accessorKey: 'documentType',
        minSize: 250,
        cell: ({ row: { original } }) => capitalize(original.documentType),
      },
      {
        header: t('common:total_sales'),
        accessorKey: 'total',
        minSize: 150,
        textAlign: 'right',
        sortName: 'total',
        cell: ({ getValue }) => string2money(getValue() as number),
      },
      {
        header: t('common:billing_address'),
        accessorKey: 'billingAddress',
        minSize: 250,
        cell: ({ row }) => {
          const billingAddress = row.original.billingAddress;
          return <BillingAddressTagname billingAddress={billingAddress} />;
        },
      },
      {
        header: t('common:guest'),
        accessorKey: 'guests',
        minSize: 250,
        cell: ({ getValue }) => (getValue() as any).join(', '),
      },
      ...commonSaleColumnTable(t),
      ...commonColumnTable(t),
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetTravelDocumentsSales,
    {
      params: {
        page,
        status: activeKey !== '6' ? status : undefined,
        filter: {
          payment_status: activeKey === '6' ? status : undefined,
        },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.DocumentSalesPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('document-sales');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:travel_document_sales')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.TravelDocumentSalesCreate}
        />
        <SaleListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              refetchNotification();
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />

        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <TravelDocumentSalesTable
          travelDocuments={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
